import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Box, Typography, Container, Paper } from '@mui/material';
import { Security } from '@mui/icons-material';
import Header from '../components/Header';

// Firebase's hosted handler completes most action modes on GET via
// applyActionCode. Auto-forwarding those re-exposes the scanner-auto-click
// vector. Only resetPassword is safe to auto-forward: Firebase renders a
// password form there and requires a POST to consume the code. Everything
// else — known or unknown — gets a confirmation button.
const SAFE_AUTO_FORWARD_MODES = new Set(['resetPassword']);

const MODE_CONFIG = {
  revertSecondFactorAddition: {
    title: 'Remove Two-Factor Authentication?',
    description: "You've received an email asking whether you recently added 2-step verification to your account. Click the button below only if you did NOT set this up yourself.",
    hint: 'If you did enable 2-step verification, you can close this tab safely — no action is needed.',
    buttonLabel: 'Yes, remove 2-step verification',
    destructive: true,
  },
  recoverEmail: {
    title: 'Revert Email Change?',
    description: 'This link was sent because the email address on your account was changed. Click the button below only if you did NOT make this change.',
    hint: 'If you changed your email yourself, you can close this tab safely.',
    buttonLabel: 'Yes, revert the email change',
    destructive: true,
  },
  verifyEmail: {
    title: 'Verify your email address',
    description: 'Click the button below to confirm this email address belongs to you.',
    buttonLabel: 'Verify email',
  },
  verifyAndChangeEmail: {
    title: 'Confirm email change',
    description: 'Click the button below to confirm the new email address on your account.',
    buttonLabel: 'Confirm email change',
  },
  signIn: {
    title: 'Complete sign-in',
    description: 'Click the button below to finish signing in.',
    buttonLabel: 'Sign in',
  },
};

const DEFAULT_CONFIG = {
  title: 'Confirm this action',
  description: 'Click the button below to continue.',
  buttonLabel: 'Confirm',
};

function buildFirebaseHandlerUrl(search) {
  const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  return `https://${authDomain}/__/auth/action${search}`;
}

function AuthAction() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode');
  const forwardUrl = buildFirebaseHandlerUrl(location.search);
  const [forwarded, setForwarded] = useState(false);

  useEffect(() => {
    if (mode && SAFE_AUTO_FORWARD_MODES.has(mode)) {
      window.location.replace(forwardUrl);
      setForwarded(true);
    }
  }, [mode, forwardUrl]);

  if (forwarded || (mode && SAFE_AUTO_FORWARD_MODES.has(mode))) {
    return (
      <div>
        <Header />
        <Container maxWidth="sm" sx={{ mt: '6rem', textAlign: 'center' }}>
          <Typography sx={{ color: '#6B7280' }}>Redirecting...</Typography>
        </Container>
      </div>
    );
  }

  const config = (mode && MODE_CONFIG[mode]) || DEFAULT_CONFIG;
  const handleConfirm = () => { window.location.assign(forwardUrl); };

  return (
    <div>
      <Header />
      <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
        <Paper
          elevation={0}
          sx={{
            padding: '3rem',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '12px',
            textAlign: 'center'
          }}
        >
          {config.destructive && (
            <Security sx={{ fontSize: '3rem', color: '#EF4444', mb: 2 }} />
          )}
          <Typography variant="h4" sx={{ mb: 2, color: '#1F2937', fontWeight: 600 }}>
            {config.title}
          </Typography>
          <Typography sx={{ mb: config.hint ? 2 : 4, color: '#6B7280' }}>
            {config.description}
          </Typography>
          {config.hint && (
            <Typography sx={{ mb: 4, color: '#6B7280', fontSize: '0.875rem' }}>
              {config.hint}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              color={config.destructive ? 'error' : 'primary'}
              size="large"
              onClick={handleConfirm}
              sx={{ py: 1.5, fontSize: '1rem', fontWeight: 500 }}
            >
              {config.buttonLabel}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => { window.location.href = '/'; }}
              sx={{ py: 1.5, fontSize: '1rem', fontWeight: 500 }}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Container>
    </div>
  );
}

export default AuthAction;
